import React from 'react';
import { Reading } from '../models/Reading';
import { Bill } from '../models/Bill';
import { getReadings, getBills } from '../dataFetcher';
import ReadingsDisplay from './ReadingsDisplay';
import Invoices from './Invoices';

const EnergyDashboard: React.FC<any> = () => {
  const [readings, setReadings] = React.useState<Reading | null>(null);
  const [bills, setBills] = React.useState<Bill[]>([]);
  const [activeView, setActiveView] = React.useState<'readings' | 'invoices'>('readings');

  const startDate = '2023-09-01';
  const endDate = '2025-09-01';

  // if you want to make the dates dynamic in the future, uncomment below
  // const [startDate, setStartDate] = React.useState<string>('2023-09-01');
  // const [endDate, setEndDate] = React.useState<string>('2025-09-01');

  React.useEffect(() => {
    // Fetch readings data
    getReadings({ start: startDate, end: endDate }).then((data: Reading[]) => {
      if (data.length > 0) {
        setReadings(data[0]);
      }
    }).catch(error => {
      console.error("Error fetching readings:", error);
    });

    // Fetch bills data
    getBills({ start: startDate, end: endDate }).then((data: Bill[] ) => {
      console.log("Fetched bills data:", data);
      setBills(data ? data : []);
    }).catch(error => {
      console.error("Error fetching bills:", error);
    });
  }, []);


  return (
    <div className={`energy-dashboard`}>
      <h2>Hello Cher, Welcome to your Sunny Energy Reports Dashboard</h2>
      {readings && (
        <div className="readings-section">
          <h3>Theater Building Meter Readings & Invoices</h3>
          <div style={{ display: 'flex', gap: '40px', marginBottom: '24px' }}>
            <div style={{ flex: 1 }}>
              <p>Reading ID: {readings.id}</p>
              <p>Building Name: {'Main Facility'}</p>
              <p>Start Date: {new Date(readings.start_date || startDate).toLocaleDateString()}</p>
              <p>End Date: {new Date(readings.end_date || endDate).toLocaleDateString()}</p>
            </div>
            <div style={{ flex: 1 }}>
              <p>Total Consumption: {readings.total_consumption} kWh</p>
              <p>Average Daily Consumption: {readings.average_daily_consumption} kWh</p>
              <p>Total Readings: {readings.getReadingCount()}</p>
              <p>Total Invoices: {bills.length}</p>
            </div>
          </div>

          {/* Overall Summary */}
          {bills && bills.length > 0 && (
            <div style={{
              marginTop: '20px',
              padding: '20px',
              backgroundColor: '#2c3e50',
              color: 'white',
              borderRadius: '8px'
            }}>
              <h3 style={{ margin: '0 0 15px 0', color: 'white' }}>📈 Overall Summary</h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px',
                fontSize: '14px',
                color: 'white'
              }}>
                <div>
                  <div style={{ opacity: 0.8, color: 'white' }}>Total Amount:</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'white' }}>
                    ${bills.reduce((sum, bill) => sum + bill.getTotalAmount(), 0).toFixed(2)}
                  </div>
                </div>
                <div>
                  <div style={{ opacity: 0.8, color: 'white' }}>Average Monthly Cost:</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'white' }}>
                    ${(bills.reduce((sum, bill) => sum + bill.getTotalAmount(), 0) / Math.max(bills.length, 1)).toFixed(2)}
                  </div>
                </div>
                <div>
                  <div style={{ opacity: 0.8, color: 'white' }}>Total Usage:</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'white' }}>
                    {bills.reduce((sum, bill) => sum + bill.use, 0).toFixed(1)} {bills[0]?.useUnit || 'kWh'}
                  </div>
                </div>
                <div>
                  <div style={{ opacity: 0.8, color: 'white' }}>Average Usage per Month:</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'white' }}>
                    {(
                      bills.reduce((sum, bill) => sum + bill.use, 0) / Math.max(bills.length, 1)
                    ).toFixed(1)} {bills[0]?.useUnit || 'kWh'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div style={{
            display: 'flex',
            gap: '10px',
            margin: '20px 0',
            paddingTop: '20px',
            borderTop: '1px solid #e0e0e0'
          }}>
            <button
              onClick={() => setActiveView('readings')}
              style={{
                padding: '12px 24px',
                backgroundColor: activeView === 'readings' ? '#3498db' : '#ecf0f1',
                color: activeView === 'readings' ? 'white' : '#2c3e50',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px',
                transition: 'all 0.3s ease'
              }}
            >
              📊 Readings
            </button>
            <button
              onClick={() => setActiveView('invoices')}
              style={{
                padding: '12px 24px',
                backgroundColor: activeView === 'invoices' ? '#3498db' : '#ecf0f1',
                color: activeView === 'invoices' ? 'white' : '#2c3e50',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px',
                transition: 'all 0.3s ease'
              }}
            >
              📄 Invoices
            </button>
          </div>

          {/* Conditional Content Display */}
          {activeView === 'readings' && (
            <div className="readings-list">
              <h4>Individual Readings:</h4>
              <ReadingsDisplay readingsData={readings} />
            </div>
          )}

          {activeView === 'invoices' && bills && bills.length > 0 && (
            <div className="invoices-section">
              <Invoices bills={bills} />
            </div>
          )}

          {activeView === 'invoices' && (!bills || bills.length === 0) && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              color: '#666',
              margin: '20px 0'
            }}>
              <h4>No invoices available</h4>
              <p>There are no bills to display for the selected period.</p>
            </div>
          )}
        </div>
      )}


    </div>
  );
};

export default EnergyDashboard;
